import { useId } from 'react';
import { RotateCcw, Undo2 } from 'lucide-react';
import { FILTER_CONTROL_CLASS } from '@/components/common/AdvancedFilter/advancedFilter.constants';
import { FloatingFieldLabel } from '@/components/common/FloatingFieldLabel';
import { IconTooltipButton } from '@/components/common/IconTooltipButton';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { FIELD_CONTROL_CLASS } from '@/styles/fieldControl';
import { FLOATING_FIELD_BOX_CLASS } from '@/styles/floatingField';
import { MENU_LABEL_MAX_LENGTH, MENU_LABEL_STATUS } from '../constants/menuLabels.constants';
import type { MenuNameActions } from '../types/menuLabel.types';
import type { MenuLabelRow } from '../utils/menuLabelRows';

const CHANGED_FIELD_CLASS = 'border-primary/45';

interface MenuNameInputProps {
  row: MenuLabelRow;
  actions: MenuNameActions;
  floatingLabel?: string | undefined;
  className?: string | undefined;
}

export function MenuNameInput({ row, actions, floatingLabel, className }: MenuNameInputProps) {
  const inputId = useId();
  const errorId = useId();
  const hasDuplicate = row.duplicateOf !== null;

  const input = (
    <Input
      id={inputId}
      value={row.inputValue}
      onChange={(event) => actions.onRename(row.menuKey, event.target.value)}
      placeholder={row.shipped}
      maxLength={MENU_LABEL_MAX_LENGTH}
      aria-label={`New name for ${row.shipped}`}
      aria-invalid={hasDuplicate}
      aria-describedby={hasDuplicate ? errorId : undefined}
      className={cn(
        floatingLabel === undefined ? FIELD_CONTROL_CLASS : FILTER_CONTROL_CLASS,
        row.status !== MENU_LABEL_STATUS.original && CHANGED_FIELD_CLASS,
      )}
    />
  );

  return (
    <div className={cn('min-w-0', className)}>
      {floatingLabel === undefined ? (
        input
      ) : (
        <div data-filled="" className={FLOATING_FIELD_BOX_CLASS}>
          {input}
          <FloatingFieldLabel htmlFor={inputId}>{floatingLabel}</FloatingFieldLabel>
        </div>
      )}
      {hasDuplicate ? (
        <p id={errorId} className="mt-1 text-xs text-destructive">
          {`"${row.duplicateOf ?? ''}" already uses this name`}
        </p>
      ) : null}
    </div>
  );
}

interface ResetNameButtonProps {
  row: MenuLabelRow;
  actions: MenuNameActions;
}

export function ResetNameButton({ row, actions }: ResetNameButtonProps) {
  if (row.status === MENU_LABEL_STATUS.unsaved) {
    return (
      <IconTooltipButton
        icon={Undo2}
        label={`Undo, back to "${row.saved}"`}
        onClick={() => actions.onUndo(row.menuKey)}
      />
    );
  }
  if (row.status === MENU_LABEL_STATUS.renamed) {
    return (
      <IconTooltipButton
        icon={RotateCcw}
        label={`Back to "${row.shipped}"`}
        onClick={() => actions.onReset(row.menuKey)}
      />
    );
  }
  return null;
}
